interface IActionCodeType {
    actionType: HTMLInputElement;
    actionCode: HTMLInputElement;
}

export default class UpsellActionQuantityRelocator {
    private static actionCodeTypeRepository: WeakMap<
        HTMLSelectElement,
        IActionCodeType
    > = new WeakMap<HTMLSelectElement, IActionCodeType>();

    public form: HTMLFormElement;

    public actionQuantities: HTMLSelectElement[] = [];

    constructor(form: HTMLFormElement) {
        this.form = form;
        this.actionQuantities = this.captureActionQuantities();

        this.processActionQuantities();
    }

    private static reportUIError(message: string, element: Element): void {
        console.error({
            message,
            element
        });
    }

    private captureActionQuantities(): HTMLSelectElement[] {
        const comboboxes = Array.from(
            this.form.querySelectorAll<HTMLSelectElement>(
                `select[name^="ActionQuantity"]`
            )
        );

        comboboxes.forEach((combobox) => {
            const index = combobox
                .getAttribute("name")
                ?.replace("ActionQuantity", "")
                .trim();

            const actionType = document.querySelector<HTMLInputElement>(
                `input[id="ActionType${index}"]`
            );
            const actionCode = document.querySelector<HTMLInputElement>(
                `input[id="ActionCode${index}"]`
            );

            if (actionType && actionCode) {
                UpsellActionQuantityRelocator.actionCodeTypeRepository.set(
                    combobox,
                    {
                        actionCode,
                        actionType
                    }
                );
            }
        });

        return comboboxes;
    }

    private processActionQuantities(): void {
        this.actionQuantities.forEach((actionQuantity) => {
            const placeholder =
                this.getPlaceholderByActionQuantity(actionQuantity);
            if (!placeholder) return;

            this.handlePlaceholderCombobox(placeholder, actionQuantity);
            this.handlePlaceholderLabels(placeholder, actionQuantity);
            this.handleHiddenInputs(placeholder, actionQuantity);
            this.removeOriginalContainer(actionQuantity);
        });
    }

    private getPlaceholderByActionQuantity(
        actionQuantity: HTMLSelectElement
    ): Element | undefined {
        const placeholder = document.querySelector(
            `[data-action-quantity-placeholder-id="${actionQuantity.id}"]`
        );

        if (!placeholder) {
            UpsellActionQuantityRelocator.reportUIError(
                `No placeholder element is available for ${actionQuantity.id}`,
                actionQuantity
            );
            return;
        }

        return placeholder;
    }

    private handlePlaceholderLabels(
        placeholder: Element,
        actionQuantity: HTMLSelectElement
    ): void {
        const labels = Array.from(placeholder.querySelectorAll("label"));
        labels.forEach((label) => label.setAttribute("for", actionQuantity.id));

        if (labels.length === 0) {
            UpsellActionQuantityRelocator.reportUIError(
                `No label elements are available for ${actionQuantity.id}`,
                placeholder
            );
        }
    }

    private handlePlaceholderCombobox(
        placeholder: Element,
        actionQuantity: HTMLSelectElement
    ): void {
        const combobox = placeholder.querySelector("select");
        if (!combobox) {
            UpsellActionQuantityRelocator.reportUIError(
                `No select element is available to replace with ${actionQuantity.id}`,
                placeholder
            );
            return;
        }

        const classList = combobox.getAttribute("class");
        if (classList) {
            actionQuantity.setAttribute("class", classList);
        }

        combobox.replaceWith(actionQuantity);
    }

    private handleHiddenInputs(
        placeholder: Element,
        actionQuantity: HTMLSelectElement
    ): void {
        const repo =
            UpsellActionQuantityRelocator.actionCodeTypeRepository.get(
                actionQuantity
            );
        if (!repo) return;

        placeholder.insertAdjacentElement("beforeend", repo.actionCode);
        placeholder.insertAdjacentElement("beforeend", repo.actionType);
    }

    private removeOriginalContainer(actionQuantity: HTMLSelectElement): void {
        const repo =
            UpsellActionQuantityRelocator.actionCodeTypeRepository.get(
                actionQuantity
            );
        if (!repo) return;

        const container = this.form.querySelector(
            `[id$="${repo.actionCode.value}"]`
        );
        if (!container) return;

        container.remove();
    }
}
